import {MenuItem, Paper} from '@material-ui/core';
import SearchResult from 'Core/components/Navigation/SearchResult';
import {
  searchAccounts,
  searchAccountsByNumber,
  searchAgents,
  searchPolicies,
  searchPoliciesByLast4OfSSN,
  searchPoliciesByPayorName,
  searchPoliciesByPolicyNumber,
  SearchResult as SearchResultItem,
  searchAgentsByLastName,
  searchPoliciesByLast4OfOwnerSSN,
} from 'Core/ducks/search';
import {matchAsync} from 'Core/models/Async';
import Downshift from 'downshift';
import * as PolicyActions from 'Agent/Policies/ducks/policy';
import React, {useEffect, useState} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import {Scope} from 'Shared/constants/Scope';
import notEmpty from 'Shared/utilities/notEmpty';
import {
  selectCurrentAgentNumbers,
  selectCurrentIdentifier,
  selectUserScopes,
} from 'Users/selectors/userProfileSelectors';
import './SearchBar.sass';

export type SearchBarVariation = 'default' | 'collapsed';

type SearchBarProps = {
  variant?: SearchBarVariation;
} & ReturnType<typeof mapStateToProps> &
  RouteComponentProps &
  typeof PolicyActions;

enum SearchType {
  AccountsByName = 1,
  PoliciesByInsuredName = 2,
  Agents = 3,
  AccountsByNumber = 4,
  PoliciesByLast4OfPayorSSN = 5,
  PoliciesByPayorName = 6,
  PoliciesByPolicyNumber = 7,
  AgentsByLastName = 8,
  PoliciesByLast4OfOwnerSSN = 9,
}

const getSearchAction = (searchType: SearchType) => {
  switch (searchType) {
    case SearchType.AccountsByName:
      return searchAccounts;
    case SearchType.PoliciesByInsuredName:
      return searchPolicies;
    case SearchType.Agents:
      return searchAgents;
    case SearchType.AgentsByLastName:
      return searchAgentsByLastName;
    case SearchType.AccountsByNumber:
      return searchAccountsByNumber;
    case SearchType.PoliciesByLast4OfPayorSSN:
      return searchPoliciesByLast4OfSSN;
    case SearchType.PoliciesByLast4OfOwnerSSN:
      return searchPoliciesByLast4OfOwnerSSN;
    case SearchType.PoliciesByPayorName:
      return searchPoliciesByPayorName;
    case SearchType.PoliciesByPolicyNumber:
      return searchPoliciesByPolicyNumber;
  }
};

const getSearchTypes = (scopes: Scope[]) => [
  scopes.includes(Scope.SearchAccounts)
    ? {
        name: 'Accounts (By Name)',
        value: SearchType.AccountsByName,
      }
    : null,
  scopes.includes(Scope.SearchAccounts)
    ? {
        name: 'Accounts (By Number)',
        value: SearchType.AccountsByNumber,
      }
    : null,
  scopes.includes(Scope.SearchPolicies)
    ? {
        name: 'Policies (By Policy Number)',
        value: SearchType.PoliciesByPolicyNumber,
      }
    : null,
  scopes.includes(Scope.SearchPolicies)
    ? {
        name: 'Policies (By Insured Name)',
        value: SearchType.PoliciesByInsuredName,
      }
    : null,
  scopes.includes(Scope.SearchPolicies)
    ? {
        name: 'Policies (By Last 4 of Payor SSN)',
        value: SearchType.PoliciesByLast4OfPayorSSN,
      }
    : null,
  scopes.includes(Scope.SearchPolicies)
    ? {
        name: 'Policies (By Last 4 of Owner SSN)',
        value: SearchType.PoliciesByLast4OfOwnerSSN,
      }
    : null,
  scopes.includes(Scope.SearchPolicies)
    ? {
        name: 'Policies (By Payor Name)',
        value: SearchType.PoliciesByPayorName,
      }
    : null,
  scopes.includes(Scope.ImpersonateAgent)
    ? {
        name: 'Agents (By Username)',
        value: SearchType.Agents,
      }
    : null,
  scopes.includes(Scope.ImpersonateAgent)
    ? {
        name: 'Agents (By Last Name)',
        value: SearchType.AgentsByLastName,
      }
    : null,
];

export const SearchBar = (props: SearchBarProps) => {
  const isPrimary = props.variant && props.variant === 'collapsed';
  const searchTypes = getSearchTypes(props.scopes).filter(notEmpty);
  const [searchType, setSearchType] = useState(searchTypes.length > 0 ? searchTypes[0].value : undefined);
  const dispatch = useDispatch();
  const {results} = useSelector((state: ReducerState) => state.search);
  const {scopes} = useSelector((state: ReducerState) => state.user.profile);
  const searchActionCreator = searchType ? getSearchAction(searchType) : undefined;

  useEffect(() => {
    if (searchTypes.length > 0) {
      setSearchType(searchTypes[0].value);
    }
  }, [scopes]);

  return (
    <div className={`search-bar ${isPrimary ? 'search-bar--collapsed' : '"'}`}>
      <div className="search-bar__input-container">
        <select
          id="search-type-select"
          className="search-bar__search-type"
          value={searchType}
          onChange={e => setSearchType(parseInt(e.target.value, 10))}
        >
          {searchTypes.map(searchType => (
            <option key={`search-item-${searchType.value}`} value={searchType.value}>
              {searchType.name}
            </option>
          ))}
        </select>
        <Downshift
          onInputValueChange={inputValue => {
            if (searchActionCreator) {
              dispatch(searchActionCreator(inputValue));
            }
          }}
          onSelect={(searchResult: SearchResultItem, state) => {
            if (searchResult) {
              dispatch(searchResult.selectRoute);
              state.clearSelection();
            }
          }}
        >
          {({getInputProps, getItemProps, getMenuProps, highlightedIndex, inputValue, isOpen, closeMenu}) => (
            <div className="search-bar__downshift-content">
              <input
                id="search-bar-input"
                disabled={searchTypes.length === 0}
                value={inputValue || ''}
                className={'search-bar__input' + (isPrimary ? '--primary' : '')}
                placeholder="Search"
                {...getInputProps()}
              />
              {!inputValue && <i className="fa fa-search search-bar__search-icon" />}
              <div className="search-bar__search-results" {...getMenuProps()}>
                {isOpen ? (
                  <Paper className="search-results__dropdown" square>
                    {matchAsync(results, {
                      Conflict: error => <ErrorPage error={error} />,
                      Loaded: searchResults =>
                        searchResults.length === 0 ? (
                          <p className="search-bar__no-results-found">No Results Found</p>
                        ) : (
                          <>
                            {searchResults.map((result, index) => (
                              <SearchResult
                                result={result}
                                onClose={closeMenu}
                                itemProps={getItemProps({item: result})}
                                key={'searchResult-' + index}
                                selected={highlightedIndex === index}
                              />
                            ))}
                            <MenuItem
                              className="search-bar__view-results-button"
                              onClick={() => {
                                props.history.push(searchResults[0].viewMoreRoute);
                                closeMenu();
                              }}
                            >
                              View All Results
                            </MenuItem>
                          </>
                        ),
                      Loading: () => <Loading />,
                      ServerError: error => <ErrorPage error={error} />,
                      Unauthorized: error => <ErrorPage error={error} />,
                    })}
                  </Paper>
                ) : null}
              </div>
            </div>
          )}
        </Downshift>
      </div>
    </div>
  );
};

export const mapStateToProps = (state: ReducerState) => ({
  agentNumbers: selectCurrentAgentNumbers(state),
  policies: state.policy.policies,
  policyFilter: state.policy.policyFilter,
  encryptedIdentifier: selectCurrentIdentifier(state),
  scopes: selectUserScopes(state),
});

export default withRouter(
  connect(mapStateToProps, {
    ...PolicyActions,
  })(SearchBar)
);
